// src/components/roads/page/AIInsightsPanel.js
// FlowSense MOW+ Insights — Live WebSockets + Backend ML Engine Feed.
// Extracted from RoadWorksPage v4.7.
// --- UPDATE: v5.0 — filter tabs (All / Live / Critical / Actionable) with counts,
// per-insight dismiss, relative timestamps, confidence meter + model tag when the
// payload carries them, recommendations list, dark-mode-safe tints, AI glyph header.
import React, { memo, useCallback, useMemo, useState } from 'react';
import {
    Badge, Box, Button, Divider, Flex, HStack, Icon, IconButton, Text, Tooltip, VStack,
    useColorModeValue,
} from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';
import {
    MdWarning, MdError, MdCheckCircle, MdSmartToy, MdOutlineAutoAwesome, MdExpandMore, MdClose,
} from 'react-icons/md';
import {
    EmptyState, MotionBox, PanelHeader, PulseIndicator, SectionCard, ShimmerSkeleton,
    formatRelative, useNow,
} from './shared';
import { AiChip, AiNeuralIcon, ConfidenceMeter, ModelTag } from './AiMlIcons';

const INSIGHT_CFG = {
    warning: { color: 'orange', icon: MdWarning },
    error:   { color: 'red',    icon: MdError },
    success: { color: 'green',  icon: MdCheckCircle },
    info:    { color: 'blue',   icon: MdSmartToy },
    live:    { color: 'pink',   icon: MdOutlineAutoAwesome },
};

// --- UPDATE: filter tabs
const FILTERS = [
    { key: 'all',        label: 'All' },
    { key: 'live',       label: 'Live' },
    { key: 'critical',   label: 'Critical' },
    { key: 'actionable', label: 'Actionable' },
];
const matches = (insight, key) => {
    if (key === 'live') return !!insight.isLive;
    if (key === 'critical') return insight.severity === 'critical' || insight.severity === 'high';
    if (key === 'actionable') return Array.isArray(insight.actions) && insight.actions.length > 0;
    return true;
};
const MODEL_HINTS = { rul: 'rsf', survival: 'rsf', anomaly: 'iforest', cluster: 'dbscan', dashcam: 'yolo', pothole: 'yolo' };
const modelFor = (insight) => {
    if (insight.model) return insight.model;
    const text = `${insight.title || ''} ${insight.message || ''}`.toLowerCase();
    const hit = Object.keys(MODEL_HINTS).find((k) => text.includes(k));
    return hit ? MODEL_HINTS[hit] : null;
};
// --- END UPDATE

export const AIInsightsPanel = memo(({ batchInsights, liveEvents, isLoading, onInsightAction }) => {
    const [expanded, setExpanded] = useState(null);
    // --- UPDATE
    const [filter, setFilter] = useState('all');
    const [dismissed, setDismissed] = useState(() => new Set());
    const now = useNow(30000);
    const filterActiveBg = useColorModeValue('purple.500', 'purple.400');
    const filterIdleBg = useColorModeValue('gray.100', 'whiteAlpha.100');
    const bodyText = useColorModeValue('gray.700', 'gray.200');
    // --- END UPDATE
    const mutedColor = useColorModeValue('gray.500', 'gray.400');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    const allInsights = useMemo(() => {
        // --- UPDATE: drop dismissed items
        return [...(liveEvents || []), ...(batchInsights || [])].filter((i) => !dismissed.has(i.id));
        // --- END UPDATE
    }, [liveEvents, batchInsights, dismissed]);

    // --- UPDATE
    const counts = useMemo(
        () => FILTERS.reduce((acc, f) => ({ ...acc, [f.key]: allInsights.filter((i) => matches(i, f.key)).length }), {}),
        [allInsights]
    );
    const visible = useMemo(() => allInsights.filter((i) => matches(i, filter)), [allInsights, filter]);

    const dismiss = useCallback((e, id) => {
        e.stopPropagation();
        setDismissed((prev) => new Set(prev).add(id));
    }, []);
    // --- END UPDATE

    const handleActionClick = (e, action) => {
        e.stopPropagation();
        if (onInsightAction) onInsightAction(action);
    };

    return (
        <SectionCard accentColor="purple">
            <PanelHeader
                // --- UPDATE: neural-network glyph + AI chip
                iconBg="purple.500" icon={AiNeuralIcon}
                badge={<AiChip kind="ai" size="xs" tooltip="Recommendations generated by FlowSense ML models" />}
                // --- END UPDATE
                title="FlowSense MOW+ Insights"
                subtitle="Live ML & Batch Analytics Feed"
                right={
                    <HStack>
                        {(liveEvents || []).length > 0 && (
                            <Tooltip label="Live AI Events Streaming" hasArrow>
                                <Box display="flex" alignItems="center">
                                    <PulseIndicator color="pink" size={2} />
                                </Box>
                            </Tooltip>
                        )}
                        <Badge colorScheme="purple" borderRadius="full" px={2} fontSize="xs">
                            {isLoading ? '…' : allInsights.length}
                        </Badge>
                    </HStack>
                }
            />
            {/* --- UPDATE: filter tabs */}
            <HStack px={4} pt={3} spacing={1.5} flexWrap="wrap" role="tablist" aria-label="Insight filters">
                {FILTERS.map((f) => {
                    const active = filter === f.key;
                    return (
                        <Button
                            key={f.key}
                            size="xs"
                            borderRadius="full"
                            role="tab"
                            aria-selected={active}
                            bg={active ? filterActiveBg : filterIdleBg}
                            color={active ? 'white' : undefined}
                            _hover={{ opacity: 0.85 }}
                            onClick={() => setFilter(f.key)}
                            isDisabled={!active && counts[f.key] === 0}
                        >
                            {f.label} {counts[f.key] > 0 ? `(${counts[f.key]})` : ''}
                        </Button>
                    );
                })}
                {dismissed.size > 0 && (
                    <Button size="xs" variant="link" colorScheme="purple" ml="auto" onClick={() => setDismissed(new Set())}>
                        Restore {dismissed.size}
                    </Button>
                )}
            </HStack>
            {/* --- END UPDATE */}
            <Box maxH="360px" overflowY="auto" p={4} css={{
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { background: 'var(--chakra-colors-gray-300)', borderRadius: '2px' },
            }}>
                {isLoading && allInsights.length === 0 ? (
                    <VStack spacing={3}>
                        {[1, 2].map(i => (
                            <Box key={i} p={4} w="full" borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
                                <ShimmerSkeleton lines={3} />
                            </Box>
                        ))}
                    </VStack>
                ) : visible.length === 0 ? (
                    <EmptyState icon={MdSmartToy} title={filter === 'all' ? 'AI Engine Standing By' : 'Nothing in this view'}
                        subtitle={filter === 'all'
                            ? 'Insights will appear here as ML models process network telemetry.'
                            : 'Try another filter.'} />
                ) : (
                    <AnimatePresence>
                        <VStack spacing={3} align="stretch">
                            {visible.map((insight) => {
                                const tc = INSIGHT_CFG[insight.type] || INSIGHT_CFG.info;
                                const isExp = expanded === insight.id;
                                const accent = insight.isLive ? 'pink' : tc.color;
                                const model = modelFor(insight);
                                const recs = Array.isArray(insight.recommendations) ? insight.recommendations : [];

                                return (
                                    <MotionBox key={insight.id} layout
                                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <Box p={4} borderRadius="xl" borderWidth="1px" borderLeftWidth="4px"
                                            borderColor={`${accent}.200`}
                                            borderLeftColor={`${accent}.500`}
                                            // --- UPDATE: dark-mode-safe tint
                                            bg={`${accent}.50`}
                                            _dark={{ bg: 'whiteAlpha.50', borderColor: `${accent}.700` }}
                                            // --- END UPDATE
                                            cursor="pointer"
                                            onClick={() => setExpanded(isExp ? null : insight.id)}
                                            transition="all 0.25s"
                                            _hover={{ transform: 'translateX(3px)', boxShadow: 'md' }}
                                            role="button"
                                            aria-expanded={isExp}
                                        >
                                            <Flex justify="space-between" align="flex-start" gap={2}>
                                                <HStack spacing={3} flex={1} minW={0}>
                                                    <MotionBox
                                                        animate={(!insight.isLive && (insight.type === 'warning' || insight.type === 'error')) ? { scale: [1, 1.2, 1] } : {}}
                                                        transition={{ duration: 2, repeat: Infinity }}
                                                    >
                                                        <Icon as={insight.isLive ? MdOutlineAutoAwesome : tc.icon} boxSize={4} color={`${accent}.500`} flexShrink={0} />
                                                    </MotionBox>
                                                    <Box minW={0}>
                                                        <HStack spacing={1.5} flexWrap="wrap">
                                                            {insight.isLive && <AiChip kind="live" size="xs" />}
                                                            <Text fontSize="xs" fontWeight="700" color={`${accent}.700`} _dark={{ color: `${accent}.200` }}>
                                                                {insight.title}
                                                            </Text>
                                                        </HStack>
                                                        {/* --- UPDATE: meta row */}
                                                        <HStack spacing={2} mt={1} flexWrap="wrap">
                                                            {model && <ModelTag model={model} />}
                                                            {insight.confidence !== undefined && <ConfidenceMeter value={insight.confidence} w="44px" />}
                                                            {insight.timestamp && (
                                                                <Text fontSize="9px" color={mutedColor}>{formatRelative(insight.timestamp, now)}</Text>
                                                            )}
                                                        </HStack>
                                                        {/* --- END UPDATE */}
                                                    </Box>
                                                </HStack>
                                                <HStack spacing={1}>
                                                    <Badge colorScheme={insight.severity === 'critical' ? 'red' : tc.color} fontSize="2xs" borderRadius="full">
                                                        {insight.severity}
                                                    </Badge>
                                                    <MotionBox animate={{ rotate: isExp ? 180 : 0 }} transition={{ duration: 0.2 }}>
                                                        <Icon as={MdExpandMore} boxSize={4} color={mutedColor} />
                                                    </MotionBox>
                                                    {/* --- UPDATE */}
                                                    <IconButton
                                                        size="xs" variant="ghost" icon={<MdClose />} minW="20px" h="20px"
                                                        aria-label="Dismiss insight" onClick={(e) => dismiss(e, insight.id)}
                                                    />
                                                    {/* --- END UPDATE */}
                                                </HStack>
                                            </Flex>

                                            <Text fontSize="xs" color={bodyText} mt={2} lineHeight="1.5" noOfLines={isExp ? 99 : 2} fontWeight="500">
                                                {insight.message}
                                            </Text>

                                            <AnimatePresence>
                                                {isExp && (recs.length > 0 || insight.actions?.length > 0) && (
                                                    <MotionBox initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} mt={3}>
                                                        <Divider mb={3} borderColor={`${accent}.200`} />
                                                        {/* --- UPDATE: recommendations (previously stored but never shown) */}
                                                        {recs.length > 0 && (
                                                            <VStack align="stretch" spacing={1} mb={insight.actions?.length ? 3 : 0}>
                                                                {recs.map((r) => (
                                                                    <Text key={r} fontSize="xs" color={bodyText}>• {r}</Text>
                                                                ))}
                                                            </VStack>
                                                        )}
                                                        {/* --- END UPDATE */}
                                                        {insight.actions?.length > 0 && (
                                                            <Flex wrap="wrap" gap={2}>
                                                                {insight.actions.map((action, ri) => (
                                                                    <MotionBox key={ri} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: ri * 0.08 }}>
                                                                        <Button
                                                                            size="xs"
                                                                            colorScheme={accent}
                                                                            variant={ri === 0 ? 'solid' : 'outline'}
                                                                            onClick={(e) => handleActionClick(e, action)}
                                                                            boxShadow={ri === 0 ? 'sm' : 'none'}
                                                                        >
                                                                            {action.label}
                                                                        </Button>
                                                                    </MotionBox>
                                                                ))}
                                                            </Flex>
                                                        )}
                                                    </MotionBox>
                                                )}
                                            </AnimatePresence>
                                        </Box>
                                    </MotionBox>
                                );
                            })}
                        </VStack>
                    </AnimatePresence>
                )}
            </Box>
        </SectionCard>
    );
});
AIInsightsPanel.displayName = 'AIInsightsPanel';

export default AIInsightsPanel;
